import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import { ArrowLeft, Filter, Download, FileText, Camera, Loader2 } from 'lucide-react';

// Tela de consulta das visitas de um formulário de qualidade do Treinamento —
// hoje usada só pela Avaliação de Treinamento (ex-Avaliação TSV; a config vem
// por prop, o que sustenta reuso futuro). ⚠️ Servia também a Auditoria SA até
// 2026-09-23 (DEM-00011, T22/ajuste visual) — ela ganhou tela própria
// (AvaliacaoSAHistorico.js) ao mudar para o módulo Operacional.
//
// MUDOU EM 2026-08-05: era uma tabela de ~35 colunas (uma por pergunta) que
// rolava na horizontal. Agora é uma LISTA de visitas — uma linha cada — e o
// detalhe pergunta a pergunta virou o PDF, que traz também as fotos das
// negativas. O que a tabela larga fazia bem (levar tudo para a planilha) segue
// no export CSV, que continua exportando as colunas de pergunta.

const COR = '#0284c7';   // azul do módulo Treinamento (ModuloHeader do hub)

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const dataBR = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

// 'AAAA-MM-DD' a partir do que o backend devolve (Date ISO ou string).
const dataISO = (d) => (d ? String(new Date(d).toISOString().slice(0, 10)) : '');

// Contagem de fotos anexadas — exportada para as telas montarem suas colunas
// sem repetir o ícone e a regra de cor (cinza quando não há evidência nenhuma).
export const ContagemEvidencias = ({ qtd }) => {
  const n = Number(qtd || 0);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'center', color: n ? '#0f172a' : '#94a3b8' }}>
      <Camera size={14} /> {n}
    </span>
  );
};

// Quem enviou a visita (vem do USUARIO_ID gravado no /submit).
// ⚠️ Visita antiga não tem autor gravado e NUNCA terá — o USUARIO_ID passou a
// ser preenchido depois delas. Mostra "—" em cinza: célula vazia se lê como
// falha de carregamento, e um nome chutado seria pior que o traço.
export const RegistradoPor = ({ nome }) => (
  nome ? <span>{nome}</span> : <span style={{ color: '#94a3b8' }}>—</span>
);

function RelatorioFormulario({
  api,              // '/api/auditoria' | '/api/avaliacao' | '/api/treinamento/ck-quiosque'
  titulo,           // 'Relatório de Auditoria de Segurança do Alimento'
  subtitulo,
  colunasCsv,       // [{ key, label }] — o que vai para a planilha
  calcularExtras,   // (linha) => linha com as colunas calculadas do CSV (seções)
  // Colunas da LISTA (a tela). Default = o formato de SA/TSV; o Quiosque passa
  // as suas, porque lá quem identifica a visita é o CK e não a dupla de
  // gerentes. A coluna do PDF é acrescentada sempre, no fim.
  colunasLista,
  campoFiltroLoja = 'LOJA',   // por qual campo o seletor de "loja" filtra
  rotuloFiltroLoja = 'Loja',
  embedded = false,
}) {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
  const [visitas, setVisitas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [filterLoja, setFilterLoja] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filtros, setFiltros] = useState({ loja: '', inicio: '', fim: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [abrindoPdf, setAbrindoPdf] = useState(null);
  const itemsPerPage = 25;

  const getMarginLeft = () => {
    if (window.innerWidth <= 480) return isCollapsed ? '60px' : '0px';
    return isCollapsed ? '100px' : '260px';
  };

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${api}/forms`, { headers: auth() });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.message || `Falha ao buscar as visitas (HTTP ${r.status}).`);
        }
        setVisitas((await r.json()).map((v) => ({ ...v, DATA: dataISO(v.DATA) })));
      } catch (e) {
        setErro(e.message);
      } finally {
        setCarregando(false);
      }
    })();
  }, [api]);

  // Lojas do próprio conjunto de dados — evita manter uma lista fixa de siglas
  // na tela (a antiga já estava defasada e não acompanhava loja nova).
  const lojas = useMemo(
    () => [...new Set(visitas.map((v) => v[campoFiltroLoja]).filter(Boolean))].sort(),
    [visitas, campoFiltroLoja]
  );

  const filtradas = useMemo(() => visitas.filter((v) => {
    if (filtros.loja && v[campoFiltroLoja] !== filtros.loja) return false;
    if (filtros.inicio && v.DATA < filtros.inicio) return false;
    if (filtros.fim && v.DATA > filtros.fim) return false;
    return true;
  }), [visitas, filtros, campoFiltroLoja]);

  const aplicarFiltros = () => {
    setFiltros({ loja: filterLoja, inicio: startDate, fim: endDate });
    setCurrentPage(1);
  };

  // Abre o PDF numa aba. Passa pelo fetch (e não por <a href>) porque a rota
  // exige o token — um link direto voltaria 401.
  const abrirPdf = useCallback(async (visita) => {
    setAbrindoPdf(visita.ID);
    try {
      // Série versionada (DEM-00014): PDF pela rota versionada, gerado sob demanda.
      const qs = visita.ORIGEM === 'versionado' ? '?origem=versionado' : '';
      const r = await fetch(`${api}/forms/${visita.ID}/pdf${qs}`, { headers: auth() });
      if (!r.ok) throw new Error('Não foi possível abrir o PDF desta visita.');
      const url = window.URL.createObjectURL(await r.blob());
      window.open(url, '_blank');
      // Revoga depois de um tempo: revogar na hora fecharia o PDF recém-aberto.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.message);
    } finally {
      setAbrindoPdf(null);
    }
  }, [api]);

  // CSV: mantém as colunas de pergunta — é a única saída dos dados para planilha.
  const exportarCsv = () => {
    if (!filtradas.length) return;
    const linhas = filtradas.map((v) => {
      const completa = calcularExtras ? calcularExtras(v) : v;
      const linha = {};
      for (const col of colunasCsv) linha[col.label] = completa[col.key] ?? '';
      return linha;
    });
    const cabecalhos = Object.keys(linhas[0]);
    const corpo = linhas.map((linha) => cabecalhos.map((campo) => {
      let valor = linha[campo] !== null && linha[campo] !== undefined ? String(linha[campo]) : '';
      valor = valor.replace(/"/g, '""');
      if (valor.search(/("|,|;|\n)/g) >= 0) valor = `"${valor}"`;
      return valor;
    }).join(';'));
    const csv = [cabecalhos.join(';'), ...corpo].join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${api.split('/').pop()}_visitas.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(filtradas.length / itemsPerPage));
  const pagina = filtradas.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const th = { padding: '14px 18px', fontWeight: 600, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
  const td = { padding: '14px 18px', color: '#334155', fontSize: 14, whiteSpace: 'nowrap' };

  // Colunas da lista. O default é o formato de SA/TSV; quem chama pode trocar
  // (o Quiosque identifica a visita pelo CK, não pela dupla de gerentes).
  // ⚠️ `tabular-nums` nas colunas numéricas: sem isso as notas não alinham pela
  // vírgula e a coluna "dança" de linha para linha.
  // Régua de cor NOVA (DEM-00011 Fase 3), espelhada da Avaliação 360
  // (Aval360Historico.js — mesma paleta, para as telas do método novo casarem).
  // Só é aplicada a registros do MÉTODO NOVO; a série antiga não tem faixa de cor.
  const faixaPorPercentual = (percentual) => {
    const p = Number(percentual);
    if (!Number.isFinite(p)) return null;
    if (p >= 90) return { bg: '#d1fae5', color: '#065f46', icone: '🟢', rotulo: 'Verde' };
    if (p >= 80) return { bg: '#fef9c3', color: '#854d0e', icone: '🟡', rotulo: 'Amarelo' };
    if (p >= 70) return { bg: '#ffedd5', color: '#9a3412', icone: '🟠', rotulo: 'Laranja' };
    return { bg: '#fee2e2', color: '#991b1b', icone: '🔴', rotulo: 'Vermelho' };
  };
  const chipBase = { display: 'inline-block', padding: '3px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' };
  const REGUA_NOVA = 'Régua nova (contagem SIM÷(SIM+NÃO)): Verde ≥90 · Amarelo 80–89,99 · Laranja 70–79,99 · Vermelho <70.';
  const REGUA_LEGADO = 'Série antiga: método de peso fixo e corte único de 80% — congelada, não recalculada (DEM-00011).';

  const colunasPadrao = [
    { titulo: 'Loja', estilo: { fontWeight: 700 }, render: (v) => v.LOJA },
    { titulo: 'Data', render: (v) => dataBR(v.DATA) },
    { titulo: 'G. Negócios', estilo: { whiteSpace: 'normal' }, render: (v) => v.GERENTE_NEGOCIO || '—' },
    { titulo: 'G. Plantão', estilo: { whiteSpace: 'normal' }, render: (v) => v.GERENTE_PLANTAO || '—' },
    {
      titulo: 'Nota', alinhamento: 'right', estilo: { fontVariantNumeric: 'tabular-nums', fontWeight: 600 },
      render: (v) => (Number.isNaN(Number(v.PORCENTAGEM_TOTAL)) ? '—' : `${Number(v.PORCENTAGEM_TOTAL).toFixed(2)}%`),
    },
    {
      // T18/T19: a cor é DERIVADA na leitura (não há coluna) e a coluna DIZ qual
      // régua vale para a linha. Método novo → faixa colorida da régua nova;
      // legado → chip cinza "Método antigo". Sem isso, 85% de peso (junho)
      // pareceria igual a 85% de contagem (novembro).
      titulo: 'Classificação', alinhamento: 'center',
      render: (v) => {
        if (!v.METODO_NOTA) {
          return <span title={REGUA_LEGADO} style={{ ...chipBase, background: '#f1f5f9', color: '#64748b' }}>Método antigo</span>;
        }
        const f = faixaPorPercentual(v.PORCENTAGEM_TOTAL);
        if (!f) return '—';
        return <span title={REGUA_NOVA} style={{ ...chipBase, background: f.bg, color: f.color }}>{f.icone} {f.rotulo}</span>;
      },
    },
    {
      titulo: 'Status', alinhamento: 'center',
      render: (v) => {
        const reprovada = String(v.STATUS || '').trim() !== 'APROVADA';
        return (
          <div>
            <span style={{
              display: 'inline-block', padding: '3px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700,
              background: reprovada ? '#fee2e2' : '#d1fae5', color: reprovada ? '#991b1b' : '#065f46',
            }}>{v.STATUS || '—'}</span>
            {/* T20: nomeia a CAUSA do IMPUR (questão crítica × percentual). O
                servidor manda MOTIVO_IMPUR pronto; mostrar só a tag esconde o porquê. */}
            {reprovada && v.MOTIVO_IMPUR && (
              <div style={{ marginTop: 4, fontSize: 11, color: '#991b1b', whiteSpace: 'normal', maxWidth: 240, marginLeft: 'auto', marginRight: 'auto' }}>
                {v.MOTIVO_IMPUR}
              </div>
            )}
          </div>
        );
      },
    },
    { titulo: 'Evidências', alinhamento: 'center', render: (v) => <ContagemEvidencias qtd={v.QTD_EVIDENCIAS} /> },
    { titulo: 'Registrado por', estilo: { whiteSpace: 'normal' }, render: (v) => <RegistradoPor nome={v.REGISTRADO_POR} /> },
  ];
  const colunas = colunasLista || colunasPadrao;
  const colSpan = colunas.length + 1;   // +1 = a coluna do PDF

  return (
    <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

      <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease' }}>
        {!embedded && (
          <div style={{ height: 80, backgroundColor: '#fff', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', padding: '0 40px', position: 'sticky', top: 0, zIndex: 10 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Consulta de Relatórios</h2>
          </div>
        )}

        <div style={{ padding: 40, maxWidth: 1200, margin: '0 auto' }}>
          {!embedded && (
            <button onClick={() => navigate('/treinamento')} style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748b', marginBottom: 20, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
              <ArrowLeft size={16} /> Voltar para Central de Formulários
            </button>
          )}

          <div style={{ marginBottom: 30 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>{titulo}</h1>
            <p style={{ color: '#64748b', fontSize: 16 }}>{subtitulo}</p>
          </div>

          {/* FILTROS */}
          <div style={{ backgroundColor: '#fff', padding: 24, borderRadius: 16, border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 30, alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>{rotuloFiltroLoja}</label>
              <select value={filterLoja} onChange={(e) => setFilterLoja(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', outline: 'none' }}>
                <option value="">Todas</option>
                {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Data inicial</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', outline: 'none' }} />
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Data final</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', outline: 'none' }} />
            </div>
            <div style={{ flex: '0 1 auto', display: 'flex', gap: 10 }}>
              <button onClick={aplicarFiltros} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', backgroundColor: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                <Filter size={16} /> Aplicar filtros
              </button>
              <button onClick={exportarCsv} disabled={filtradas.length === 0} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', backgroundColor: COR, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: filtradas.length === 0 ? 'not-allowed' : 'pointer', opacity: filtradas.length === 0 ? 0.5 : 1 }}>
                <Download size={16} /> Exportar CSV
              </button>
            </div>
          </div>

          {/* LISTA DE VISITAS */}
          <div style={{ backgroundColor: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 860 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', color: '#64748b', fontSize: 13, textTransform: 'uppercase' }}>
                    {colunas.map((c) => (
                      <th key={c.titulo} style={{ ...th, textAlign: c.alinhamento || 'left' }}>{c.titulo}</th>
                    ))}
                    <th style={{ ...th, textAlign: 'center' }}>Relatório</th>
                  </tr>
                </thead>
                <tbody>
                  {carregando && (
                    <tr><td colSpan={colSpan} style={{ ...td, textAlign: 'center', color: '#64748b' }}>Carregando…</td></tr>
                  )}
                  {!carregando && erro && (
                    <tr><td colSpan={colSpan} style={{ ...td, textAlign: 'center', color: '#b91c1c' }}>{erro}</td></tr>
                  )}
                  {!carregando && !erro && pagina.length === 0 && (
                    <tr><td colSpan={colSpan} style={{ ...td, textAlign: 'center', color: '#64748b' }}>Nenhuma visita no filtro selecionado.</td></tr>
                  )}
                  {pagina.map((v) => (
                    // ⚠️ chave composta: ID não é único entre a série legada e a
                    // versionada (DEM-00014); só ORIGEM+ID identifica a linha.
                    <tr key={`${v.ORIGEM || 'leg'}-${v.ID}`} style={{ borderBottom: '1px solid #f1f5f9' }}
                      onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f0f9ff'; }}
                      onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                      {colunas.map((c) => (
                        <td key={c.titulo} style={{ ...td, textAlign: c.alinhamento || 'left', ...(c.estilo || {}) }}>
                          {c.render(v)}
                        </td>
                      ))}
                      <td style={{ ...td, textAlign: 'center' }}>
                        <button
                          onClick={() => abrirPdf(v)}
                          disabled={abrindoPdf === v.ID}
                          title="Abrir o PDF da visita (com as fotos das perguntas não conformes)"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                            borderRadius: 6, border: `1px solid ${COR}`, background: '#fff', color: COR,
                            fontWeight: 700, fontSize: 12, cursor: abrindoPdf === v.ID ? 'wait' : 'pointer',
                          }}
                        >
                          {abrindoPdf === v.ID ? <Loader2 size={14} className="spin" /> : <FileText size={14} />}
                          {abrindoPdf === v.ID ? 'Abrindo…' : 'Abrir PDF'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>
                Página {currentPage} de {totalPages}
                <span style={{ marginLeft: 10, fontWeight: 600 }}>(Total: {filtradas.length} visitas)</span>
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}
                  style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #cbd5e1', backgroundColor: '#fff', color: currentPage === 1 ? '#94a3b8' : '#0f172a', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500 }}>
                  Anterior
                </button>
                <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
                  style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #cbd5e1', backgroundColor: '#fff', color: currentPage === totalPages ? '#94a3b8' : '#0f172a', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500 }}>
                  Próxima
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RelatorioFormulario;
